/**
 * Intrinsic value lab: model metadata, formulas, and calculators (educational).
 * All currency inputs use consistent units noted per field; outputs are per-share intrinsic value in dollars.
 */

export const INTRINSIC_MODEL_ORDER = ["fcff", "fcfe", "rim", "adjusted_nav", "epv", "graham"];

export const INTRINSIC_MODELS = {
  fcff: {
    id: "fcff",
    label: "FCFF (firm DCF → equity per share)",
    description:
      "Discounts projected free cash flows to the firm (FCFF) at the weighted average cost of capital (WACC), adds a terminal value, then subtracts net debt to obtain equity value. Dividing by diluted shares gives intrinsic value per share.",
    formula:
      "EV = Σ_{t=1..N} FCFF_t / (1 + WACC)^t + TV / (1 + WACC)^N,  TV = FCFF_{N+1} / (WACC − g_term),  Equity = EV − Net debt,  IV/share = Equity / shares.",
    abbreviations: [
      "FCFF — Free cash flow to the firm (cash available to all capital providers after operating investment).",
      "WACC — Weighted average cost of capital (blended discount rate for debt and equity).",
      "EV — Enterprise value (value of the operating business to all investors).",
      "TV — Terminal value (PV of cash flows beyond the explicit forecast, often Gordon growth).",
      "g_high — Short-term growth rate applied to FCFF in the forecast window.",
      "g_term — Long-run perpetual growth rate in the terminal period (must stay below WACC).",
      "N — Number of explicit forecast years.",
      "IV — Intrinsic value (here: equity value per diluted share).",
    ],
    assumptions: [
      "FCFF grows at g_high during the explicit forecast; terminal cash flow uses g_term (perpetual, must be < WACC).",
      "WACC is constant; capital structure and discount rates are stable.",
      "Net debt is a reasonable proxy for claims senior to common equity.",
      "Single path of cash flows (point estimate), not a distribution.",
    ],
    fields: [
      { key: "fcff1_m", label: "FCFF in year 1 ($ millions)", type: "number", default: 100000, step: 100 },
      { key: "forecast_years", label: "Explicit forecast years (N)", type: "number", default: 5, step: 1 },
      { key: "g_high", label: "FCFF growth g_high (decimal, e.g. 0.05)", type: "number", default: 0.05, step: 0.005 },
      { key: "wacc", label: "WACC (decimal)", type: "number", default: 0.09, step: 0.005 },
      { key: "g_terminal", label: "Terminal growth g_term (decimal)", type: "number", default: 0.025, step: 0.005 },
      { key: "net_debt_m", label: "Net debt ($ millions)", type: "number", default: 80000, step: 1000 },
      { key: "shares_m", label: "Diluted shares outstanding (millions of shares)", type: "number", default: 15000, step: 10 },
    ],
    compute: (v) => computeFcffToEquityPerShare(v),
  },

  fcfe: {
    id: "fcfe",
    label: "FCFE (equity DCF)",
    description:
      "Discounts free cash flow to equity (FCFE) at the cost of equity. FCFE is cash available to common shareholders after debt service and reinvestment. The result is equity value directly; divide by shares for value per share.",
    formula:
      "Equity = Σ_{t=1..N} FCFE_t / (1 + r_e)^t + TV / (1 + r_e)^N,  TV = FCFE_{N+1} / (r_e − g_term),  IV/share = Equity / shares.",
    abbreviations: [
      "FCFE — Free cash flow to equity (cash available to common shareholders after debt service and reinvestment).",
      "r_e — Cost of equity (required return on equity, used as the discount rate here).",
      "TV — Terminal value (PV of FCFE after the explicit forecast).",
      "g_high — Short-term growth rate applied to FCFE in the forecast window.",
      "g_term — Perpetual growth in the terminal period (must stay below r_e).",
      "N — Number of explicit forecast years.",
      "IV — Intrinsic value per diluted share.",
    ],
    assumptions: [
      "Leverage path is implicit in the FCFE series you supply; r_e is constant.",
      "Terminal growth g_term must be less than r_e.",
      "Same single-path forecast caveats as FCFF.",
    ],
    fields: [
      { key: "fcfe1_m", label: "FCFE in year 1 ($ millions)", type: "number", default: 90000, step: 100 },
      { key: "forecast_years", label: "Explicit forecast years (N)", type: "number", default: 5, step: 1 },
      { key: "g_high", label: "FCFE growth g_high (decimal)", type: "number", default: 0.05, step: 0.005 },
      { key: "re", label: "Cost of equity r_e (decimal)", type: "number", default: 0.095, step: 0.005 },
      { key: "g_terminal", label: "Terminal growth g_term (decimal)", type: "number", default: 0.025, step: 0.005 },
      { key: "shares_m", label: "Diluted shares outstanding (millions of shares)", type: "number", default: 15000, step: 10 },
    ],
    compute: (v) => computeFcfeEquityPerShare(v),
  },

  rim: {
    id: "rim",
    label: "RIM (residual income, simplified)",
    description:
      "Residual income values the firm by starting from book value and adding the present value of economic profits: earnings in excess of a charge on beginning book value at the cost of equity. This implementation uses a compact perpetuity on excess return (ROE vs. cost of equity) with long-run book growth.",
    formula:
      "IV ≈ B_ps + B_ps × (ROE − r_e) / (r_e − g),  where B_ps is book value per share, ROE is expected return on equity, r_e is cost of equity, and g is long-run growth in book value (must be < r_e).",
    abbreviations: [
      "RIM — Residual income model (value = book value plus PV of “abnormal” earnings vs. cost of equity).",
      "ROE — Return on equity (net income ÷ equity; forward-looking expectation here).",
      "B_ps — Book value per share (common equity ÷ shares).",
      "r_e — Cost of equity (required return; charge on book in residual income).",
      "g — Long-run growth rate of book value (g_book in inputs).",
      "IV — Intrinsic value per share.",
    ],
    assumptions: [
      "ROE and g are stable; clean surplus holds approximately.",
      "Excess return fades only through the (r_e − g) term—this is a stylized shortcut, not a full multi-year RI forecast.",
    ],
    fields: [
      { key: "book_ps", label: "Book value per share ($)", type: "number", default: 4.5, step: 0.1 },
      { key: "roe", label: "Expected ROE (decimal)", type: "number", default: 0.45, step: 0.01 },
      { key: "re", label: "Cost of equity r_e (decimal)", type: "number", default: 0.095, step: 0.005 },
      { key: "g_book", label: "Long-run book value growth g (decimal)", type: "number", default: 0.03, step: 0.005 },
    ],
    compute: (v) => computeRimPerShare(v),
  },

  adjusted_nav: {
    id: "adjusted_nav",
    label: "Adjusted net asset value",
    description:
      "Marks assets and liabilities closer to fair value than GAAP book (e.g. real estate, reserves). Here you enter the resulting net asset value per share after adjustments—common for REITs, resources, and holding companies.",
    formula: "IV/share = Adjusted net asset value per share (direct input).",
    abbreviations: [
      "NAV — Net asset value (assets minus liabilities; “adjusted” means fair-value or revaluation vs. GAAP book).",
      "GAAP — Generally accepted accounting principles (reported financial statement basis).",
      "IV — Intrinsic value per share (here set equal to your adjusted NAV per share).",
    ],
    assumptions: [
      "Adjustments capture fair value gaps; liquidity and control discounts are not modeled unless you net them into the number you enter.",
    ],
    fields: [{ key: "nav_ps", label: "Adjusted NAV per share ($)", type: "number", default: 180, step: 0.5 }],
    compute: (v) => computeNavPerShare(v),
  },

  epv: {
    id: "epv",
    label: "EPV (earnings power value)",
    description:
      "Greenwald-style earnings power: normalize sustainable operating earnings after tax (NOPAT), capitalize at the cost of capital with no growth, then allocate to common equity. Growth is treated separately; here EPV is the no-growth capitalized earnings slice.",
    formula:
      "EPV_firm ≈ NOPAT / WACC,  IV/share ≈ EPV_firm / shares (after net debt if you fold it into your NOPAT story—this form assumes NOPAT is levered-firm appropriate and you scale to per share with shares only; adjust NOPAT if you need debt subtraction manually).",
    abbreviations: [
      "EPV — Earnings power value (capitalized sustainable earnings with no growth in this template).",
      "NOPAT — Net operating profit after tax (operating earnings × (1 − tax rate), unlevered operating profit concept).",
      "WACC — Weighted average cost of capital (discount / capitalization rate for the firm).",
      "IV — Intrinsic value per share.",
    ],
    assumptions: [
      "NOPAT is a sustainable mid-cycle estimate, not a peak year.",
      "No growth in EPV component; competitive advantage growth would be added separately in a full EPV build.",
      "Here EPV_firm ≈ NOPAT/WACC; we divide by shares for per-share EPV (treat as simplified asset-side capitalized earnings).",
    ],
    fields: [
      { key: "nopat_m", label: "Normalized NOPAT ($ millions, after tax)", type: "number", default: 120000, step: 100 },
      { key: "wacc", label: "WACC (decimal)", type: "number", default: 0.09, step: 0.005 },
      { key: "shares_m", label: "Diluted shares (millions of shares)", type: "number", default: 15000, step: 10 },
    ],
    compute: (v) => computeEpvPerShare(v),
  },

  graham: {
    id: "graham",
    label: "Graham intrinsic value",
    description:
      "Ben Graham’s formula (as often cited) relates EPS, an earnings multiplier tied to growth g, the AAA bond yield Y, and a 4.4% reference yield. In this app, g is tied to long-run EPS history: Average2 is the mean of diluted EPS over Y−2, Y−1, and Y using whichever of those years have data (one to three values); Average1 is the same for Y−12…Y−10; then g = (Average2/Average1)^0.1 − 1 (decimal). The 8.5 + 2g term uses g as a percent (g% = 100×g when |g| ≤ 1). On the historical chart, g is further smoothed as a 12‑month rolling average of those quarter-level g values to reduce step noise.",
    formula:
      "g = (Average2/Average1)^0.1 − 1;  V = EPS × (8.5 + 2×g%) × 4.4 / Y.  Y = Moody's Aaa yield in percent. Auto-fill uses trailing EPS and annual EPS history from statements.",
    abbreviations: [
      "EPS — Earnings per share used in V (here often TTM / recent EPS from statements in auto-fill).",
      "g — Growth rate from EPS history: g = (Average2/Average1)^0.1 − 1. Average2 averages EPS for Y−2,Y−1,Y and Average1 for Y−12,Y−11,Y−10 using only years that have data (missing years are skipped).",
      "Y — Moody's Aaa corporate bond yield, in percent (e.g. 5 means 5%).",
      "AAA — Highest credit-quality long corporate bonds (yield used as the interest-rate anchor).",
      "4.4 — Historical reference yield in Graham’s formulation (pairs with Y in the ratio).",
      "V — Graham intrinsic value estimate per share.",
    ],
    assumptions: [
      "EPS is a normalized earnings base; formula is a heuristic, not a DCF.",
      "Uses a long bond yield as the discount-rate proxy in the denominator.",
    ],
    fields: [
      { key: "eps", label: "EPS ($)", type: "number", default: 6.5, step: 0.05 },
      {
        key: "g",
        label: "g (decimal): (Avg EPS Y−2..Y / Avg EPS Y−12..Y−10)^0.1 − 1; or edit manually",
        type: "number",
        default: 0.07,
        step: 0.005,
      },
      { key: "y_aaa", label: "AAA bond yield Y (percent, e.g. 5.0)", type: "number", default: 5.0, step: 0.1 },
    ],
    compute: (v) => computeGraham(v),
  },
};

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : NaN;
}

function computeFcffToEquityPerShare(v) {
  const fcff1 = num(v.fcff1_m) * 1e6;
  const n = Math.max(1, Math.floor(num(v.forecast_years)));
  const g = num(v.g_high);
  const wacc = num(v.wacc);
  const gt = num(v.g_terminal);
  const netDebt = num(v.net_debt_m) * 1e6;
  const shares = num(v.shares_m) * 1e6;
  if (!(fcff1 > 0) || !(shares > 0)) return { error: "FCFF and shares must be positive." };
  if (!(wacc > gt)) return { error: "WACC must exceed terminal growth (g_term < WACC)." };
  if (!(wacc > 0)) return { error: "WACC must be positive." };
  let pv = 0;
  for (let t = 1; t <= n; t++) {
    const fcffT = fcff1 * Math.pow(1 + g, t - 1);
    pv += fcffT / Math.pow(1 + wacc, t);
  }
  const fcffN = fcff1 * Math.pow(1 + g, n - 1);
  const fcffN1 = fcffN * (1 + gt);
  const tv = fcffN1 / (wacc - gt);
  pv += tv / Math.pow(1 + wacc, n);
  const equity = pv - netDebt;
  if (!(equity > 0)) return { error: "Equity value is not positive with these inputs." };
  return { ivPerShare: equity / shares };
}

function computeFcfeEquityPerShare(v) {
  const fcfe1 = num(v.fcfe1_m) * 1e6;
  const n = Math.max(1, Math.floor(num(v.forecast_years)));
  const g = num(v.g_high);
  const re = num(v.re);
  const gt = num(v.g_terminal);
  const shares = num(v.shares_m) * 1e6;
  if (!(fcfe1 > 0) || !(shares > 0)) return { error: "FCFE and shares must be positive." };
  if (!(re > gt)) return { error: "Cost of equity must exceed terminal growth." };
  if (!(re > 0)) return { error: "Cost of equity must be positive." };
  let pv = 0;
  for (let t = 1; t <= n; t++) {
    const fcfeT = fcfe1 * Math.pow(1 + g, t - 1);
    pv += fcfeT / Math.pow(1 + re, t);
  }
  const fcfeN = fcfe1 * Math.pow(1 + g, n - 1);
  const fcfeN1 = fcfeN * (1 + gt);
  const tv = fcfeN1 / (re - gt);
  pv += tv / Math.pow(1 + re, n);
  if (!(pv > 0)) return { error: "Equity value is not positive with these inputs." };
  return { ivPerShare: pv / shares };
}

function computeRimPerShare(v) {
  const B = num(v.book_ps);
  const roe = num(v.roe);
  const re = num(v.re);
  const g = num(v.g_book);
  if (!(B > 0)) return { error: "Book value per share must be positive." };
  if (!(re > g)) return { error: "Cost of equity must exceed long-run book growth (g < r_e)." };
  const denom = re - g;
  const iv = B + (B * (roe - re)) / denom;
  if (!Number.isFinite(iv)) return { error: "Could not compute RIM value." };
  return { ivPerShare: iv };
}

function computeNavPerShare(v) {
  const nav = num(v.nav_ps);
  if (!(nav > 0)) return { error: "NAV per share must be positive." };
  return { ivPerShare: nav };
}

function computeEpvPerShare(v) {
  const nopatM = num(v.nopat_m);
  const wacc = num(v.wacc);
  const sharesM = num(v.shares_m);
  if (!(nopatM > 0) || !(wacc > 0) || !(sharesM > 0)) return { error: "NOPAT, WACC, and shares must be positive." };
  const epvFirm = (nopatM * 1e6) / wacc;
  const iv = epvFirm / (sharesM * 1e6);
  return { ivPerShare: iv };
}

function computeGraham(v) {
  const eps = num(v.eps);
  const g = num(v.g);
  const Y = num(v.y_aaa);
  if (!(eps > 0) || !(Y > 0)) return { error: "EPS and AAA yield must be positive." };
  const gPct = g <= 1 ? g * 100 : g;
  const mult = 8.5 + 2 * gPct;
  const iv = (eps * mult * 4.4) / Y;
  if (!Number.isFinite(iv)) return { error: "Could not compute Graham value." };
  return { ivPerShare: iv };
}

export function computeIntrinsicForModel(modelId, values) {
  const m = INTRINSIC_MODELS[modelId];
  if (!m?.compute) return { error: "Unknown model." };
  return m.compute(values);
}

export function defaultValuesForModel(modelId) {
  const m = INTRINSIC_MODELS[modelId];
  if (!m) return {};
  const o = {};
  for (const f of m.fields) o[f.key] = f.default;
  return o;
}
